import { AssetGroup } from '@plerionhq/types-asset-group';
import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

export type GetAssetGroupResponse = {
  data: AssetGroup;
};

export type ListAssetGroupsResponse = {
  data: AssetGroup[];
};

export type ListAssetGroupsRequest = {
  enabled?: boolean;
  name?: string;
  perPage?: number;
  refetchInterval?: number;
};

export const useListAssetGroups = (request: ListAssetGroupsRequest = {}) => {
  const queryResult = useListAssetGroupsInternalHook(request);
  const data = useMemo(() => {
    if (!Array.isArray(queryResult.data)) {
      return;
    }

    // Sort alphabetically by name with slice to avoid mutation
    return queryResult.data.slice().sort((a, b) => {
      const valueA = (a.name || '').toLowerCase();
      const valueB = (b.name || '').toLowerCase();
      return valueA.localeCompare(valueB);
    });
  }, [queryResult.data]);

  return { ...queryResult, data };
};

/**
 * Internal hook that fetches asset groups from the API.
 * The returned data is cached by react-query and should not be mutated directly.
 * Public hooks transform the data using `useMemo` to avoid mutating the cache.
 */
const useListAssetGroupsInternalHook = (request: ListAssetGroupsRequest) => {
  const queryClient = useQueryClient();
  const { name, refetchInterval, enabled = true, perPage = 100 } = request;
  const params: Record<string, string | number> = { perPage };

  if (name) {
    params.name = name;
  }

  return useQuery({
    queryKey: ['asset-groups', perPage, name],
    queryFn: async () => {
      const response = await apiClient.get<ListAssetGroupsResponse>(
        `/api/inventory/tenant/asset-groups`,
        { params },
      );
      const assetGroups = response.data.data;

      // Populate individual asset group caches from list data
      assetGroups.forEach((assetGroup) => {
        queryClient.setQueryData(
          ['asset-group', assetGroup.assetGroupId],
          assetGroup,
        );
      });

      return assetGroups;
    },
    ...(refetchInterval ? { enabled, refetchInterval } : { enabled }),
  });
};

export const useGetAssetGroup = (assetGroupId?: string) =>
  useQuery({
    queryKey: ['asset-group', assetGroupId],
    queryFn: async () => {
      if (assetGroupId) {
        const response = await apiClient.get<GetAssetGroupResponse>(
          `/api/inventory/tenant/asset-groups/${assetGroupId}`,
        );
        return response.data.data;
      }
    },
  });
